import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Ship, Package, Warehouse, FileCheck, Globe, Truck, Plane, Anchor, ArrowRight, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const services = [
  {
    icon: Ship,
    title: 'Ocean Freight',
    description: 'Comprehensive ocean freight services including Full Container Load (FCL) and Less than Container Load (LCL) shipping across all major global trade routes.',
    features: ['FCL & LCL Options', 'Competitive Rates', 'Global Coverage', 'Real-time Tracking'],
    image: 'https://images.pexels.com/photos/1117210/pexels-photo-1117210.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
  {
    icon: Package,
    title: 'Container Shipping',
    description: 'Standard, refrigerated, and specialized containers for every type of cargo. Door-to-door service with complete logistics management.',
    features: ['20ft & 40ft Containers', 'Refrigerated Units', 'Open Top & Flat Rack', 'Door-to-Door Service'],
    image: 'https://customer-assets.emergentagent.com/job_maritime-connect-2/artifacts/101hhl67_Copilot_20251106_011256.png',
  },
  {
    icon: Warehouse,
    title: 'Warehousing & Distribution',
    description: 'Secure storage facilities at major ports with comprehensive inventory management, pick-and-pack services, and distribution solutions.',
    features: ['Strategic Locations', 'Inventory Management', 'Pick & Pack Services', '24/7 Security'],
    image: 'https://images.pexels.com/photos/209251/pexels-photo-209251.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
  {
    icon: FileCheck,
    title: 'Customs Clearance',
    description: 'Expert handling of customs documentation, duties calculation, and regulatory compliance for seamless international imports and exports.',
    features: ['Documentation Handling', 'Duty & Tax Calculation', 'Regulatory Compliance', 'Express Clearance'],
    image: 'https://images.pexels.com/photos/6169668/pexels-photo-6169668.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
  {
    icon: Globe,
    title: 'Project Cargo',
    description: 'Specialized solutions for oversized, heavy-lift, and complex project shipments. Expert engineering and logistics planning for unique cargo.',
    features: ['Heavy Lift Capability', 'Project Engineering', 'Route Surveys', 'Custom Solutions'],
    image: 'https://customer-assets.emergentagent.com/job_maritime-connect-2/artifacts/r5hqmknb_step-img.png',
  },
  {
    icon: Truck,
    title: 'Land Transportation',
    description: 'Complete inland logistics from ports to final destination. Trucking, rail, and intermodal solutions with real-time visibility.',
    features: ['Trucking Services', 'Rail Transport', 'Intermodal Solutions', 'GPS Tracking'],
    image: 'https://customer-assets.emergentagent.com/job_maritime-connect-2/artifacts/pwej0hj9_delivery.png',
  },
];

export default function ServicesPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="services-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-32 lg:py-40 overflow-hidden bg-navy">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        <div className="rsl-container relative z-10">
          <span className="text-safety-orange font-mono text-xs tracking-widest uppercase">
            What We Offer
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-white uppercase tracking-tight mt-2">
            Our Services
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mt-6">
            Comprehensive maritime logistics solutions tailored to your business needs. From port to port or door to door, we deliver excellence.
          </p>
        </div>
      </section>

      {/* Services List */}
      <section className="py-20 lg:py-32">
        <div className="rsl-container">
          {services.map((service, index) => (
            <div
              key={service.title}
              className={`grid lg:grid-cols-2 gap-12 items-center ${
                index !== 0 ? 'mt-20 lg:mt-32 pt-20 lg:pt-32 border-t border-slate-200' : ''
              }`}
              data-testid={`service-detail-${index}`}
            >
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="bg-safety-orange/10 p-4 w-fit mb-6">
                  <service.icon className="w-10 h-10 text-safety-orange" />
                </div>
                <h2 className="text-3xl lg:text-4xl font-black text-navy uppercase tracking-tight mb-4">
                  {service.title}
                </h2>
                <p className="text-slate-600 text-lg mb-6">
                  {service.description}
                </p>
                <ul className="grid grid-cols-2 gap-3 mb-8">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-slate-700">
                      <CheckCircle className="w-5 h-5 text-safety-orange flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/quote">
                  <Button className="bg-safety-orange hover:bg-orange-600 text-white font-bold uppercase tracking-widest text-sm h-12 px-8">
                    Request Quote
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <div className="relative">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-80 lg:h-96 object-cover"
                  />
                  <div className="absolute inset-0 bg-navy/10" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-slate-50">
        <div className="rsl-container text-center">
          <span className="text-safety-orange font-mono text-xs tracking-widest uppercase">
            Ready to Ship?
          </span>
          <h2 className="text-3xl lg:text-4xl font-black text-navy uppercase tracking-tight mt-2 mb-6">
            Let's Discuss Your Shipping Needs
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto mb-8">
            Our logistics experts are ready to create a customized solution for your cargo. Get a free quote today.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/quote">
              <Button className="bg-safety-orange hover:bg-orange-600 text-white font-bold uppercase tracking-widest text-sm h-14 px-10">
                Get Free Quote
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-2 border-navy text-navy hover:bg-navy hover:text-white font-bold uppercase tracking-widest text-sm h-14 px-10">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
